/* eslint-disable max-depth */
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import requester from "web/script/modules/requester";
import userProfiler from "web/script/modules/userprofiler";
import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import { connect } from "react-redux";
import { closeEmailCaptureOverlay } from "web/redux/ducks/email-capture-overlay";
import environment from "web/script/modules/environment";
import url from "web/script/utils/url";
import withReduxProvider from "../../redux-provider";
import SVGIcon from "../svg-icon/svg-icon";
import styles from "./email-capture-overlay.css";
import EmailCaptureForm from "../email-capture-form/email-capture-form";
import MarketingAgreement from "../marketing-agreement/marketing-agreement";

const CAPTURE_TYPE_SIGNUP_LEAD_SAVE = "signup_lead_save";
const CAPTURE_TYPE_SIGNUP_PDP_SAVE = "signup_pdp_save";
const CAPTURE_TYPE_SIGNUP_FEED_SAVE = "signup_feed_save";
const CAPTURE_TYPE_SIGNUP_DU = "signup_du";
const CAPTURE_TYPE_SIGNUP_SEARCH = "signup_search";
const CAPTURE_TYPE_DU_POPUP = "du_popup";

function createMarkup(value) {
    return { __html: value };
}

function mapStateToProps(state) {
    const {
        captureType,
        designerId,
        designerName,
        next,
        open,
        productId,
        registrationReason,
        searchGender,
        searchTerm
    } = state.emailCaptureOverlayReducer;
    return {
        captureType,
        designerId,
        designerName,
        next,
        overlayOpen: open,
        productId,
        registrationReason,
        searchGender,
        searchTerm
    };
}

function mapDispatchToProps(dispatch) {
    return {
        closeEmailCaptureOverlay: () => dispatch(closeEmailCaptureOverlay())
    };
}

export function EmailCaptureOverlay({
    captureType,
    closeEmailCaptureOverlay,
    cookiePolicyLink,
    designerId,
    designerName,
    next,
    overlayOpen,
    privacyPolicyLink,
    productId,
    registrationReason,
    searchGender,
    searchTerm,
    termsAndConditionsLink,
    twoStepEnabled
}) {
    const country = environment.get("country");
    const emailLabelText = gettext("account.form.email_label");
    let submitText;
    let title;
    let reason = registrationReason;
    let type = "back_in_stock";
    let useTwoStep = false;

    switch (captureType) {
        case CAPTURE_TYPE_SIGNUP_LEAD_SAVE:
            submitText = gettext("account.register.return_lead_btn_txt_get_deals");
            title = gettext("account.register.return_lead_title");
            reason = "save_for_later_lead_return";
            break;
        case CAPTURE_TYPE_SIGNUP_PDP_SAVE:
        case CAPTURE_TYPE_SIGNUP_FEED_SAVE:
            submitText = gettext("account.register.return_lead_btn_txt_track_deals");
            title = gettext("saved_items.sign_up_save_item_title_option_b");
            reason = "save_for_later";
            // NOTE: Two step can only be seen on sale alert overlays
            useTwoStep = twoStepEnabled && type === "back_in_stock";
            break;
        case CAPTURE_TYPE_SIGNUP_DU:
            submitText = gettext("account.register.return_lead_btn_follow_products_designer");
            title = gettext("account.register.return_lead_title_follow_products_designer");
            type = reason;
            reason = undefined;
            break;
        case CAPTURE_TYPE_SIGNUP_SEARCH:
            submitText = gettext("account.register.return_lead_btn_follow_search");
            title = gettext("account.register.return_lead_title_follow_search", {
                search_terms: searchTerm
            });
            reason = undefined;
            type = "saved_search";
            break;
        case CAPTURE_TYPE_DU_POPUP:
            submitText = gettext("account.register.return_lead_btn_txt_alert_me");
            title = gettext("account.register.return_lead_title_subscribe_designer", {
                designer_name: designerName
            });
            type = "designer_update_lyst";
            reason = "designer_update_popup";
            break;
    }
    // Hooks
    const [emailAddress, setEmailAddress] = useState("");
    const [privacyPolicy, setPrivacyPolicy] = useState(false);
    const [marketingEmails, setMarketingEmails] = useState(false);
    const [formErrors, setFormErrors] = useState([]);
    const [step, setStep] = useState(1);

    function changeEmail(event) {
        setEmailAddress(event.target.value);
    }
    // Visibility Change handling
    let hidden;
    let visibilityChange;
    if (typeof document.hidden !== "undefined") {
        hidden = "hidden";
        visibilityChange = "visibilitychange";
    } else if (typeof document.msHidden !== "undefined") {
        hidden = "msHidden";
        visibilityChange = "msvisibilitychange";
    } else if (typeof document.webkitHidden !== "undefined") {
        hidden = "webkitHidden";
        visibilityChange = "webkitvisibilitychange";
    }

    function handleVisibilityChange() {
        if (!document[hidden]) {
            analytics.event(captureType, "view_form", "back_overlay");
            document.removeEventListener(visibilityChange, handleVisibilityChange);
        }
    }
    const appElement = useRef(null);
    useEffect(() => {
        Modal.setAppElement(appElement.current);
        return () => {
            document.removeEventListener(visibilityChange, handleVisibilityChange);
        };
    });

    function triggerAfterOpen() {
        if (useTwoStep) {
            analytics.event(captureType, "open_fill_email_form", "open_overlay");
        } else {
            analytics.event(captureType, "open_signup_form", "open_overlay");
        }
        document.addEventListener(visibilityChange, handleVisibilityChange, false);
    }

    function triggerClose() {
        if (useTwoStep) {
            if (step === 2) {
                analytics.event(captureType, "close_marketing_agreement", "exit_signup");
            } else {
                analytics.event(captureType, "close_fill_email_form", "exit_signup");
            }
            setStep(1);
        } else {
            analytics.event(captureType, "close_signup_form", "exit_signup");
        }
        document.removeEventListener(visibilityChange, handleVisibilityChange);
        closeEmailCaptureOverlay();
    }

    function handleLegalChange(privacyPolicyChecked, marketingEmailsChecked) {
        setPrivacyPolicy(privacyPolicyChecked);
        setMarketingEmails(marketingEmailsChecked);
    }

    function onNoOptionClick() {
        analytics.event(captureType, "click_no_option", "du_emails_option");
        setStep(1);
        closeEmailCaptureOverlay();
    }

    async function onSubmit(event) {
        event.preventDefault();
        analytics.event(captureType, "click_signup_button", "signup_button");

        const seenProducts = userProfiler.getSeenProducts();
        let productIds = [];
        if (seenProducts) {
            productIds = seenProducts.reduce((memo, product) => {
                memo.push(product.productId);
                return memo;
            }, []);
        }

        const body = {
            country,
            designer_id: designerId,
            email: emailAddress,
            invisible_pids: productIds.join(","),
            marketing_emails: marketingEmails,
            privacy_policy: privacyPolicy,
            reason: reason,
            two_step_enabled: useTwoStep
        };

        let jsonData = undefined;
        const queryParams = url
            .toQueryString({
                type: type,
                product_id: productId,
                designer_id: designerId,
                search_query: searchTerm,
                search_gender: searchGender,
                email_capture: "true",
                next: next
            })
            .toString();
        try {
            jsonData = await requester.post(`/account/register/?${queryParams}`, body);
        } catch (e) {
            console.error(e);
            setFormErrors([gettext("account.login.with_magic_link.error_title")]);
            analytics.event(captureType, "error_signup", "server_error");
            return;
        }
        if (jsonData && jsonData.success) {
            if (useTwoStep) {
                setStep(2);
                analytics.event(captureType, "open_marketing_agreement ", "open_overlay");
            } else {
                closeEmailCaptureOverlay();
            }
        } else {
            let errorList = [];
            const { errors } = jsonData.data;
            Object.keys(errors).map(key => {
                if (key === "marketing_emails") {
                    analytics.event(captureType, "missed_tickbox", "tickbox");
                }
                errors[key].map(message => {
                    errorList.push(message);
                });
            });
            setFormErrors(errorList);
        }
    }

    async function onSubmitMarketingOptIn(event) {
        event.preventDefault();
        analytics.event(captureType, "click_yes_option", "du_emails_option");

        const body = {
            email: emailAddress
        };

        let jsonData = undefined;

        try {
            jsonData = await requester.post("/email-capture/marketing-opt-in/", body);
        } catch (e) {
            console.error(e);
            analytics.event(captureType, "error_signup", "server_error");
            return;
        }
        if (jsonData && jsonData.success) {
            setStep(1);
            closeEmailCaptureOverlay();
        }
    }

    if (!overlayOpen) {
        return null;
    }

    const captureForm = (
        <div className={styles.captureForm}>
            <div className={styles.header}>
                <h3 dangerouslySetInnerHTML={createMarkup(title)} />
            </div>
            <EmailCaptureForm
                captureType={captureType}
                cookiePolicyLink={cookiePolicyLink}
                emailAddress={emailAddress}
                emailLabelText={emailLabelText}
                formErrors={formErrors}
                onEmailChange={changeEmail}
                onLegalChange={handleLegalChange}
                onSubmit={onSubmit}
                privacyPolicyLink={privacyPolicyLink}
                submitText={submitText}
                termsAndConditionsLink={termsAndConditionsLink}
                twoStepEnabled={useTwoStep}
            />
        </div>
    );

    const marketingForm = (
        <div className={styles.captureForm}>
            <MarketingAgreement
                onSubmit={onSubmitMarketingOptIn}
                onNoOptionClick={onNoOptionClick}
            />
        </div>
    );

    return (
        <div id="email-capture-overlay" ref={appElement}>
            <Modal
                appElement={document.body}
                isOpen={overlayOpen}
                onAfterOpen={triggerAfterOpen}
                onRequestClose={triggerClose}
                className={styles.modal}
                overlayClassName={styles.overlay}
            >
                <button className={styles.closeButton} onClick={triggerClose}>
                    <SVGIcon name="cross" flip />
                </button>
                {step === 1 && captureForm}
                {step === 2 && marketingForm}
            </Modal>
        </div>
    );
}

export default withReduxProvider(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(EmailCaptureOverlay)
);

EmailCaptureOverlay.propTypes = {
    captureType: PropTypes.oneOf([
        "signup_lead_save",
        "signup_pdp_save",
        "signup_feed_save",
        "signup_du",
        "signup_search",
        "du_popup"
    ]),
    closeEmailCaptureOverlay: PropTypes.func,
    cookiePolicyLink: PropTypes.string,
    designerId: PropTypes.number,
    designerName: PropTypes.string,
    next: PropTypes.string,
    overlayOpen: PropTypes.bool,
    privacyPolicyLink: PropTypes.string,
    productId: PropTypes.string,
    registrationReason: PropTypes.string,
    searchGender: PropTypes.string,
    searchTerm: PropTypes.string,
    termsAndConditionsLink: PropTypes.string,
    twoStepEnabled: PropTypes.bool
};
